import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * ThemeService - Read-only service for detecting dark mode state
 *
 * Note: Dark mode is managed by @ekowitryna/ui library's eko-dark-mode-toggle component.
 * This service only reads the current theme state from the DOM.
 * Do not use this service to toggle dark mode - let the library handle that.
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  private document = inject(DOCUMENT);
  private darkModeSubject = new BehaviorSubject<boolean>(this.isDarkMode);
  private observer?: MutationObserver;

  /**
   * Observable that emits when dark mode state changes
   */
  public darkMode$: Observable<boolean> = this.darkModeSubject.asObservable();

  constructor() {
    // Observe changes to the 'dark' class on the HTML element
    this.observer = new MutationObserver(() => {
      const isDark = this.isDarkMode;
      if (this.darkModeSubject.value !== isDark) {
        this.darkModeSubject.next(isDark);
      }
    });

    this.observer.observe(this.document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  /**
   * Checks if dark mode is currently active by reading the 'dark' class from HTML element
   */
  get isDarkMode(): boolean {
    return this.document.documentElement.classList.contains('dark');
  }

}
